import type LogRocket from "logrocket";

import { compiledClientConfig } from "config";
import { type UserModel } from "models/UserModel";
import { getCurrentVersion } from "utils/version";

let logRocket: typeof LogRocket | undefined;
let logrocketInitPromise: Promise<void> | undefined;
let scope: TLogrocketScope | undefined;

const SANITIZED_REQUEST_URLS = ["/v1/adaptersTokens", "/v1/agentTokens", "/v1/apiTokens", "/v1/userAccessTokens"];
const SANITIZED_HEADERS = ["authorization", "entitle_access_token", "token", "x-csrf-token"];

type TLogrocketScope = { companyId: string; user: UserModel };

export const isLogrocketEnabled = (flag: boolean | undefined) =>
  Boolean(compiledClientConfig.logrocketClientId) && flag === true;

const identifyLogrocketUser = ({ companyId, user }: TLogrocketScope) => {
  logRocket?.identify(user.id, { companyId, isAdmin: user.isAdmin, role: user.role });
};

const initializeLogrocket = async () => {
  const clientId = compiledClientConfig.logrocketClientId;
  if (clientId) {
    const [{ default: instance }, version] = await Promise.all([import("logrocket"), getCurrentVersion()]);
    const environment = compiledClientConfig.appEnv;
    instance.init(clientId, {
      browser: { urlSanitizer: removeUrlParams },
      console: { isEnabled: { debug: false, info: false, log: false } },
      dom: { inputSanitizer: true, textSanitizer: true },
      network: {
        requestSanitizer: (request) => {
          removeSanitizedHeaders(request.headers);
          if (SANITIZED_REQUEST_URLS.some((url) => request.url.includes(url))) {
            return null;
          }
          return { ...request, body: undefined, referrer: undefined, url: removeUrlParams(request.url) };
        },
        responseSanitizer: (response) => {
          removeSanitizedHeaders(response.headers);
          if (typeof response.body === "string" && response.body.includes("AbortError")) return null;
          return { ...response, body: undefined };
        },
      },
      release: `${version}_${environment}`,
      shouldCaptureIP: false,
    });
    logRocket = instance;
    if (scope) identifyLogrocketUser(scope);
  }
};

export const startLogrocket = () => {
  logrocketInitPromise ??= initializeLogrocket().catch(() => {
    logrocketInitPromise = undefined;
  });
  return logrocketInitPromise;
};

export const setLogrocketUserScope = (user: UserModel, companyId: string) => {
  scope = { companyId, user };
  if (logRocket) identifyLogrocketUser(scope);
};

export const logrocketLog = (message: string, extra?: Record<string, boolean | number | string>) => {
  if (!logRocket) return;
  logRocket.captureMessage(message, {
    extra,
  });
};

export const logrocketLogError = (error: Error, extra?: Record<string, boolean | number | string>) => {
  if (!logRocket) return;
  logRocket.captureException(error, { extra: { stack: error.stack || new Error().stack || "", ...extra } });
};

export const logrocketGetSessionUrl = (): null | string => {
  if (!logRocket) return null;
  return logRocket.sessionURL;
};

const removeSanitizedHeaders = (headers: Record<string, unknown>) => {
  for (const header of Object.keys(headers)) {
    if (SANITIZED_HEADERS.includes(header.toLowerCase())) delete headers[header];
  }
};

export const removeUrlParams = (url: string) => url.split(/[?#]/)[0];
